import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { safePrint } from "@/utils/safePrint";
import { formatDateDDMonYY } from "@/utils/dateFormat";
import { DateInputDDMonYY } from "@/components/DateInputDDMonYY";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  Truck,
  CalendarIcon,
  Printer,
  Send,
  Users,
  Package,
  RefreshCw,
  Loader2,
  FileText,
  Download,
  Save,
  ClipboardList,
  Edit,
  CheckCircle,
  XCircle,
  Plus,
  Trash2,
  FileSpreadsheet,
  Upload,
  FileCheck
} from "lucide-react";

export default function CRM() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editForm, setEditForm] = useState({
    transport_name: '',
    lr_number: '',
    invoice_number: ''
  });
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  // Trip Summary view state
  const [viewMode, setViewMode] = useState('orders'); // 'orders', 'trip_summary'
  const [tripEdits, setTripEdits] = useState({});
  const [savingTrip, setSavingTrip] = useState(null);
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [postingToSheets, setPostingToSheets] = useState(null);
  // Products for grade selection
  const [invSentLocal, setInvSentLocal] = useState({}); // Local-only state: { orderId: 'Yes'/'No' }
  const [products, setProducts] = useState([]);
  const [packSizes, setPackSizes] = useState({});
  // Print-only grade overrides (local state, NOT saved to DB)
  const [printGradeOverrides, setPrintGradeOverrides] = useState({});
  // Trip posting to sheets state
  const [postingTrip, setPostingTrip] = useState(null);
  const [pendingDialogOpen, setPendingDialogOpen] = useState(false);
  // Track which order row is in edit mode in trip summary
  const [editingTripOrderId, setEditingTripOrderId] = useState(null);
  // Invoice generation state
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceDispatchNo, setInvoiceDispatchNo] = useState('');
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  // Auto-refresh every 10 seconds for real-time updates
  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, customersRes, productsRes, packRes] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/products`),
        axios.get(`${API}/products/pack-sizes`)
      ]);
      setOrders(ordersRes.data);
      setCustomers(customersRes.data);
      setProducts(productsRes.data);
      setPackSizes(packRes.data || {});
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Get customer transporter info
  const getCustomerTransporter = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.transporter_name || '';
  };

  // Resolve dispatch_name: order field > customer record > fallback to customer_name
  const getDispatchName = (order) => {
    if (order?.dispatch_name) return order.dispatch_name;
    if (order?.customer_id) {
      const customer = customers.find(c => c.id === order.customer_id);
      if (customer?.dispatch_name) return customer.dispatch_name;
    }
    return order?.customer_name || '';
  };

  // Get dispatched orders for the date - sorted by trip sequence
  // Use dispatched_at for date filtering (NOT updated_at) to prevent orders 
  // from disappearing when editing Transport/LR/Invoice details
  const tripOrder = ['T1', 'T2', 'T3', 'T4', 'T5', '1', '2', '3', '4', '5'];
  const dispatchedOrders = orders
    .filter(order => {
      if (order.status !== 'dispatched') return false;
      // Prefer dispatched_at, fall back to updated_at, then created_at
      const dispatchDateStr = order.dispatched_at || order.updated_at || order.created_at;
      if (!dispatchDateStr) return false;
      // Extract just the date part (YYYY-MM-DD) from the stored date string
      const dispatchDateOnly = dispatchDateStr.substring(0, 10);
      // Format selected date as YYYY-MM-DD in local timezone
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const selectedDateOnly = `${year}-${month}-${day}`;
      return dispatchDateOnly === selectedDateOnly;
    })
    .sort((a, b) => {
      const tripA = tripOrder.indexOf(a.trip_no) >= 0 ? tripOrder.indexOf(a.trip_no) : 999;
      const tripB = tripOrder.indexOf(b.trip_no) >= 0 ? tripOrder.indexOf(b.trip_no) : 999;
      if (tripA !== tripB) return tripA - tripB;
      return (a.loading_seq || 999) - (b.loading_seq || 999);
    });

  // Select an order and load its details into form
  const selectOrder = (order) => {
    setSelectedOrder(order);
    const customerTransporter = getCustomerTransporter(order.customer_id);
    setEditForm({
      transport_name: order.transport_name || customerTransporter || '',
      lr_number: order.lr_number || '',
      invoice_number: order.invoice_number || ''
    });
  };

  // Save dispatch details
  const saveDispatchDetails = async () => {
    if (!selectedOrder) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/orders/${selectedOrder.id}`, editForm);
      // Update local state immediately
      setOrders(prev => prev.map(o => o.id === selectedOrder.id ? { ...o, ...editForm } : o));
      setSelectedOrder(prev => ({ ...prev, ...editForm }));
      toast.success("Dispatch details saved");
      // Refresh data from server to confirm save
      setTimeout(() => fetchData(), 500);
    } catch (error) {
      toast.error("Failed to save details");
    } finally {
      setSaving(false);
    }
  };

  // ============ TRIP SUMMARY FUNCTIONS ============
  
  // Group dispatched orders by trip for Trip Summary view
  const tripGroups = dispatchedOrders.reduce((groups, order) => {
    const tripNo = order.trip_no || 'Unassigned';
    if (!groups[tripNo]) groups[tripNo] = [];
    groups[tripNo].push(order);
    return groups;
  }, {});
  
  // Get trip edit value (from edits or original order, with customer transporter fallback)
  const getTripFieldValue = (order, field) => {
    if (tripEdits[order.id] && tripEdits[order.id][field] !== undefined) {
      return tripEdits[order.id][field];
    }
    if (order[field]) return order[field];
    // For transport_name, fallback to customer transporter
    if (field === 'transport_name') {
      return getCustomerTransporter(order.customer_id);
    }
    return '';
  };
  
  // Handle trip field change
  const handleTripFieldChange = (orderId, field, value) => {
    setTripEdits(prev => ({
      ...prev,
      [orderId]: {
        ...(prev[orderId] || {}),
        [field]: value
      }
    }));
  };
  
  // Save single order dispatch details from trip summary
  const saveTripOrderDetails = async (orderId) => {
    const edits = tripEdits[orderId];
    if (!edits || Object.keys(edits).length === 0) return;
    
    setSavingTrip(orderId);
    try {
      await axios.put(`${API}/orders/${orderId}`, edits);
      // Update local state
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, ...edits } : o));
      // Clear edits for this order
      setTripEdits(prev => {
        const newEdits = { ...prev };
        delete newEdits[orderId];
        return newEdits;
      });
      toast.success("Details saved");
      setEditingTripOrderId(null);
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSavingTrip(null);
    }
  };
  
  // Save all edited orders in a trip
  const saveAllTripOrders = async (tripNo) => {
    const tripOrders = tripGroups[tripNo] || [];
    const ordersToSave = tripOrders.filter(o => tripEdits[o.id] && Object.keys(tripEdits[o.id]).length > 0);
    
    if (ordersToSave.length === 0) {
      toast.info("No changes to save");
      return;
    }
    
    setSavingTrip(tripNo);
    try {
      await Promise.all(ordersToSave.map(order => 
        axios.put(`${API}/orders/${order.id}`, tripEdits[order.id])
      ));
      // Update local state
      setOrders(prev => prev.map(o => {
        if (tripEdits[o.id]) {
          return { ...o, ...tripEdits[o.id] };
        }
        return o;
      }));
      // Clear all edits for saved orders
      setTripEdits(prev => {
        const newEdits = { ...prev };
        ordersToSave.forEach(o => delete newEdits[o.id]);
        return newEdits;
      });
      toast.success(`Saved ${ordersToSave.length} orders`);
    } catch (error) {
      toast.error("Failed to save some orders");
    } finally {
      setSavingTrip(null);
    }
  };
  
  // Check if order has unsaved edits
  const hasUnsavedEdits = (orderId) => {
    return tripEdits[orderId] && Object.keys(tripEdits[orderId]).length > 0;
  };
  
  // Check if trip has any unsaved edits
  const tripHasUnsavedEdits = (tripNo) => {
    const tripOrders = tripGroups[tripNo] || [];
    return tripOrders.some(o => hasUnsavedEdits(o.id));
  };

  // Check if an order has incomplete dispatch details
  const isOrderIncomplete = (order) => {
    return !order.transport_name || !order.lr_number || !order.invoice_number;
  };

  // Check if trip has any incomplete orders
  const tripHasIncomplete = (tripNo) => {
    const tripOrders = tripGroups[tripNo] || [];
    return tripOrders.some(o => isOrderIncomplete(o));
  };

  // Check if trip is fully posted to sheets
  const isTripPosted = (tripNo) => {
    const tripOrders = tripGroups[tripNo] || [];
    return tripOrders.length > 0 && tripOrders.every(o => o.trip_posted_to_sheets);
  };

  // Get all incomplete dispatch numbers across all trips
  const getIncompleteDispatches = () => {
    return dispatchedOrders.filter(o => isOrderIncomplete(o)).map(o => ({
      dispatch_number: o.dispatch_number || '-',
      order_number: o.order_number || '-',
      customer: o.customer_name || '-',
      trip: o.trip_no || 'Unassigned',
      missing: [
        !o.transport_name ? 'Transport' : null,
        !o.lr_number ? 'LR No' : null,
        !o.invoice_number ? 'INV No' : null
      ].filter(Boolean)
    }));
  };

  // Generate FORMAT-2 invoice
  const [invoiceError, setInvoiceError] = useState('');
  
  const handleGenerateInvoice = async () => {
    const trimmedDispatch = invoiceDispatchNo.trim().toUpperCase();
    if (!trimmedDispatch || !invoiceFile) {
      toast.error("Please enter dispatch number and upload e-Invoice PDF");
      return;
    }
    if (!invoiceFile.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Please upload a PDF file");
      return;
    }
    setGeneratingInvoice(true);
    setInvoiceError('');
    try {
      const formData = new FormData();
      formData.append('dispatch_number', trimmedDispatch);
      formData.append('einvoice_file', invoiceFile);
      
      const response = await axios.post(`${API}/invoice/generate`, formData, {
        responseType: 'blob',
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      });
      
      // Verify we got a PDF back
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await response.data.text();
        const json = JSON.parse(text);
        throw new Error(json.detail || 'Server returned an error');
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename=(.+)/);
      link.download = filenameMatch ? filenameMatch[1] : `${trimmedDispatch}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Invoice generated and downloaded!");
      setInvoiceDialogOpen(false);
      setInvoiceDispatchNo('');
      setInvoiceFile(null);
      setInvoiceError('');
    } catch (error) {
      let errMsg = "Failed to generate invoice";
      try {
        if (error.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errMsg = json.detail || errMsg;
        } else if (error.response?.data?.detail) {
          errMsg = error.response.data.detail;
        } else if (error.message) {
          errMsg = error.message;
        }
      } catch { /* use default */ }
      setInvoiceError(errMsg);
      toast.error(errMsg);
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Post trip summary to Google Sheets
  const postTripToSheets = async (tripNo) => {
    const tripOrders = tripGroups[tripNo] || [];
    if (tripOrders.length === 0) return;
    
    setPostingTrip(tripNo);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await axios.post(`${API}/trip-summary/post-to-sheets`, {
        trip_no: tripNo,
        date: dateStr,
        order_ids: tripOrders.map(o => o.id)
      });
      // Update local state to mark as posted
      setOrders(prev => prev.map(o => {
        if (tripOrders.find(to => to.id === o.id)) {
          return { ...o, trip_posted_to_sheets: true, trip_posted_at: new Date().toISOString() };
        }
        return o;
      }));
      toast.success(`Trip ${tripNo} posted to Google Sheets`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to post trip to sheets");
    } finally {
      setPostingTrip(null);
    }
  };

  // Open edit dialog for dispatched order
  const openEditDialog = (order) => {
    setEditingOrder({
      ...order,
      items: order.items?.length > 0 ? [...order.items] : [{
        grade: order.product_grade || '',
        sku: order.packing || 'TIN',
        qty: order.quantity_kg || 0,
        rate: order.unit_price || 0
      }],
      order_date: order.order_date || format(new Date(order.created_at), 'yyyy-MM-dd')
    });
    setEditDialogOpen(true);
  };

  // Update editing order field
  const updateEditingOrder = (field, value) => {
    setEditingOrder(prev => ({ ...prev, [field]: value }));
  };

  // Update item in editing order
  const updateEditingItem = (index, field, value) => {
    setEditingOrder(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      // Auto-calculate amount
      if (field === 'qty' || field === 'rate') {
        newItems[index].amount = (newItems[index].qty || 0) * (newItems[index].rate || 0);
      }
      return { ...prev, items: newItems };
    });
  };

  // Add new item row
  const addEditingItem = () => {
    setEditingOrder(prev => ({
      ...prev,
      items: [...prev.items, { grade: '', sku: 'TIN', qty: 0, rate: 0, amount: 0 }]
    }));
  };

  // Remove item row
  const removeEditingItem = (index) => {
    if (editingOrder.items.length <= 1) return;
    setEditingOrder(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Save edited order
  const saveEditedOrder = async () => {
    if (!editingOrder) return;
    
    setSaving(true);
    try {
      // Calculate totals
      const totalQty = editingOrder.items.reduce((sum, item) => sum + (item.qty || 0), 0);
      const totalValue = editingOrder.items.reduce((sum, item) => sum + ((item.qty || 0) * (item.rate || 0)), 0);
      
      // Recalculate discount based on new items
      const discountPerKg = editingOrder.discount_per_kg || 10;
      const discountTerms = ['ADV', 'DDAP', 'DDAP RECVD'];
      
      let totalDiscount = 0;
      if (discountTerms.includes(editingOrder.payment_terms)) {
        editingOrder.items.forEach(item => {
          const packKg = getPackingKg(item);
          totalDiscount += discountPerKg * packKg * (item.qty || 0);
        });
      }
      
      const updateData = {
        customer_name: editingOrder.customer_name,
        order_date: editingOrder.order_date,
        items: editingOrder.items,
        payment_terms: editingOrder.payment_terms,
        est_type: editingOrder.est_type,
        discount_per_kg: discountPerKg,
        total_discount: totalDiscount,
        total_qty: totalQty,
        total_value: totalValue,
        invoice_value: totalValue - totalDiscount
      };
      
      await axios.put(`${API}/orders/${editingOrder.id}`, updateData);
      
      // Update local state
      setOrders(prev => prev.map(o => o.id === editingOrder.id ? { ...o, ...updateData } : o));
      if (selectedOrder?.id === editingOrder.id) {
        setSelectedOrder(prev => ({ ...prev, ...updateData }));
      }
      
      toast.success('Order updated successfully');
      setEditDialogOpen(false);
      setEditingOrder(null);
    } catch (error) {
      toast.error('Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  // Post dispatched order to Google Sheets (DISPATCH QTY tab)
  const postDispatchToSheets = async (order) => {
    setPostingToSheets(order.id);
    try {
      const response = await axios.post(`${API}/orders/${order.id}/post-dispatch-to-sheets`);
      if (response.data.success) {
        toast.success(`${order.order_number} posted to DISPATCH QTY sheet`);
        // Show alerts for auto-added grade columns
        if (response.data.added_column_alerts?.length > 0) {
          response.data.added_column_alerts.forEach(alert => {
            toast.info(alert, { duration: 8000 });
          });
        }
        // Update local state
        setOrders(prev => prev.map(o => o.id === order.id ? { ...o, dispatch_posted_to_sheets: true } : o));
        if (selectedOrder?.id === order.id) {
          setSelectedOrder(prev => ({ ...prev, dispatch_posted_to_sheets: true }));
        }
      } else {
        toast.error(response.data.message || 'Failed to post');
      }
    } catch (error) {
      toast.error('Failed to post to Google Sheets');
    } finally {
      setPostingToSheets(null);
    }
  };

  // Get unique grades from products
  const uniqueGrades = [...new Set(products.map(p => p.grade))].sort();

  // Print invoice
  const printInvoice = () => {
    if (!selectedOrder) return;
    window.open(`${API}/orders/${selectedOrder.id}/invoice-pdf`, '_blank');
  };

  // Get items with print-only grade/sku overrides applied
  const getPrintItems = (order) => {
    if (!order?.items) return order?.items || [];
    const overrides = printGradeOverrides[order.id];
    if (!overrides) return order.items;
    return order.items.map((item, idx) => ({
      ...item,
      grade: overrides[idx]?.grade ?? item.grade,
      sku: overrides[idx]?.sku ?? item.sku,
    }));
  };

  // Update a print-only grade override
  const updatePrintGrade = (orderId, itemIndex, newGrade) => {
    setPrintGradeOverrides(prev => ({
      ...prev,
      [orderId]: {
        ...(prev[orderId] || {}),
        [itemIndex]: { ...(prev[orderId]?.[itemIndex] || {}), grade: newGrade }
      }
    }));
  };

  // Update a print-only SKU/Type override
  const updatePrintSku = (orderId, itemIndex, newSku) => {
    setPrintGradeOverrides(prev => ({
      ...prev,
      [orderId]: {
        ...(prev[orderId] || {}),
        [itemIndex]: { ...(prev[orderId]?.[itemIndex] || {}), sku: newSku }
      }
    }));
  };

  // Reset print overrides for an order
  const resetPrintOverrides = (orderId) => {
    setPrintGradeOverrides(prev => {
      const copy = { ...prev };
      delete copy[orderId];
      return copy;
    });
  };

  // Print Estimate for CRM
  const printEstimate = (order) => {
    if (!order) order = selectedOrder;
    if (!order) return;
    
    // Use print-overridden items for grade display in print
    const printItems = getPrintItems(order);
    
    const qty = printItems?.length > 0 
      ? printItems.reduce((sum, i) => sum + (i.qty || 0), 0) 
      : (order.total_qty || order.quantity_kg || 0);
    const value = printItems?.length > 0
      ? printItems.reduce((sum, i) => sum + (i.amount || i.qty * i.rate || 0), 0)
      : (order.total_value || order.total_price || 0);
    const timestamp = new Date().toLocaleString('en-IN', { 
      day: '2-digit', month: 'short', year: 'numeric', 
      hour: '2-digit', minute: '2-digit', hour12: true 
    });
    
    // Format date as DD-Mon-YY — use dispatched_at for dispatched orders
    const dateSource = order.dispatched_at || order.order_date || order.created_at;
    const formattedDate = formatDateDDMonYY(dateSource);

    // Generate filename: EST_INITIALS_QTY
    const nameInitials = (getDispatchName(order) || 'X')
      .split(/\s+/)
      .map(w => w.charAt(0).toUpperCase())
      .join('');
    const estFilename = `EST_${nameInitials}${qty}`;
    
    // Build grades table (uses print-overridden items)
    const gradesHtml = printItems?.length > 0 
      ? printItems.map((item, idx) => `
          <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 8px; text-align: center;">${idx + 1}</td>
            <td style="padding: 8px; font-weight: bold;">${item.grade}</td>
            <td style="padding: 8px;">${item.sku || 'TIN'}</td>
            <td style="padding: 8px; text-align: right;">${item.qty}</td>
            <td style="padding: 8px; text-align: right;">${formatCurrency(item.rate)}</td>
            <td style="padding: 8px; text-align: right; font-weight: bold;">${formatCurrency(item.qty * item.rate)}</td>
          </tr>
        `).join('')
      : `<tr>
          <td colspan="6" style="padding: 8px; text-align: center;">No items</td>
        </tr>`;
    
    // Calculate discount and final value
    const totalDiscount = order.total_discount || 0;
    const finalValue = value - totalDiscount;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${estFilename}</title>
        <style>
          @media print {
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; font-size: 12px; }
          .estimate { max-width: 700px; margin: 0 auto; }
          .header { text-align: center; background: #1a365d; color: white; padding: 15px; margin-bottom: 20px; }
          .header h1 { font-size: 24px; margin-bottom: 5px; }
          .info-section { margin-bottom: 15px; padding: 10px 15px; background: #f7fafc; border-radius: 4px; border: 1px solid #e2e8f0; }
          .info-row { display: flex; margin: 4px 0; }
          .info-label { font-weight: bold; color: #555; width: 100px; flex-shrink: 0; }
          .info-value { }
          .grades-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          .grades-table th { background: #2d3748; color: white; padding: 10px; text-align: left; }
          .grades-table th:nth-child(4), .grades-table th:nth-child(5), .grades-table th:nth-child(6) { text-align: right; }
          .total-row { background: #e8f4e8; font-weight: bold; font-size: 14px; }
          .total-row td { padding: 12px 8px; }
          .discount-row { background: #fff3cd; }
          .discount-row td { padding: 8px; }
          .final-row { background: #d4edda; font-weight: bold; font-size: 14px; }
          .final-row td { padding: 12px 8px; }
          .footer { text-align: center; font-size: 10px; color: #666; margin-top: 20px; border-top: 1px dashed #ccc; padding-top: 10px; }
        </style>
      </head>
      <body>
        <div class="estimate">
          <div class="header">
            <h1>ESTIMATE</h1>
          </div>
          
          <div class="info-section">
              <div class="info-row"><span class="info-label">Date:</span><span class="info-value">${formattedDate}</span></div>
              <div class="info-row"><span class="info-label">Party Name:</span><span class="info-value">${getDispatchName(order)}</span></div>
              <div class="info-row"><span class="info-label">Transport:</span><span class="info-value">${order.transport_name || getCustomerTransporter(order.customer_id) || '-'}</span></div>
              <div class="info-row"><span class="info-label">LR Number:</span><span class="info-value">${order.lr_number || '-'}</span></div>
          </div>
          
          <table class="grades-table">
            <thead>
              <tr>
                <th style="width: 40px;">#</th>
                <th>Grade</th>
                <th>Packing</th>
                <th style="width: 70px;">Qty</th>
                <th style="width: 90px;">Rate</th>
                <th style="width: 100px;">Value</th>
              </tr>
            </thead>
            <tbody>
              ${gradesHtml}
              <tr class="total-row">
                <td colspan="3" style="text-align: right;">TOTAL:</td>
                <td style="text-align: right;">${qty}</td>
                <td></td>
                <td style="text-align: right;">${formatCurrency(value)}</td>
              </tr>
              ${totalDiscount > 0 ? `
              <tr class="discount-row">
                <td colspan="5" style="text-align: right;">Discount:</td>
                <td style="text-align: right; color: #c53030;">- ${formatCurrency(totalDiscount)}</td>
              </tr>
              <tr class="final-row">
                <td colspan="5" style="text-align: right;">FINAL TOTAL:</td>
                <td style="text-align: right; color: #276749;">${formatCurrency(finalValue)}</td>
              </tr>
              ` : ''}
            </tbody>
          </table>
          
          <div class="footer">
            Printed: ${timestamp}
          </div>
        </div>
      </body>
      </html>
    `;
    
    safePrint(printContent, `Estimate_${order.order_number}`);
  };

  // Download Estimate as PDF (using print to PDF)
  const downloadEstimatePDF = (order) => {
    printEstimate(order);
  };

  // Send estimate to customer via WhatsApp Web
  const sendInvoice = async () => {
    if (!selectedOrder) return;
    
    const qty = selectedOrder.items?.length > 0 
      ? selectedOrder.items.reduce((sum, i) => sum + (i.qty || 0), 0) 
      : (selectedOrder.total_qty || selectedOrder.quantity_kg || 0);
    const value = selectedOrder.items?.length > 0
      ? selectedOrder.items.reduce((sum, i) => sum + (i.amount || i.qty * i.rate || 0), 0)
      : (selectedOrder.total_value || selectedOrder.total_price || 0);
    const totalDiscount = selectedOrder.total_discount || 0;
    const finalValue = value - totalDiscount;
    
    // Format date as DD-Mon-YY
    const formattedDate = formatDateDDMonYY(selectedOrder.order_date || selectedOrder.created_at);
    
    // Build items list for message
    const itemsList = selectedOrder.items?.length > 0
      ? selectedOrder.items.map((item, idx) => 
          `${idx + 1}. ${item.grade} (${item.sku || 'TIN'}) - ${item.qty} pcs @ ₹${item.rate?.toLocaleString('en-IN')} = ₹${(item.qty * item.rate)?.toLocaleString('en-IN')}`
        ).join('\n')
      : `${selectedOrder.product_grade} - ${selectedOrder.quantity_kg} @ ₹${selectedOrder.unit_price?.toLocaleString('en-IN')}`;
    
    // Build WhatsApp message
    let message = `*ESTIMATE*\n`;
    message += `Date: ${formattedDate}\n`;
    message += `Party: ${getDispatchName(selectedOrder)}\n\n`;
    message += `*Items:*\n${itemsList}\n\n`;
    message += `*Total Qty:* ${qty}\n`;
    message += `*Total Value:* ₹${value?.toLocaleString('en-IN')}\n`;
    
    if (totalDiscount > 0) {
      message += `*Discount:* -₹${totalDiscount?.toLocaleString('en-IN')}\n`;
      message += `*Final Amount:* ₹${finalValue?.toLocaleString('en-IN')}\n`;
    }
    
    if (selectedOrder.transport_name) {
      message += `\n*Transport:* ${selectedOrder.transport_name}`;
    }
    if (selectedOrder.lr_number) {
      message += `\n*LR No:* ${selectedOrder.lr_number}`;
    }
    
    // Get customer phone number (remove spaces, +, etc.)
    let phone = selectedOrder.customer_phone || '';
    phone = phone.replace(/[\s\-\+]/g, '');
    
    // If phone starts with 0, replace with 91
    if (phone.startsWith('0')) {
      phone = '91' + phone.substring(1);
    }
    // If phone doesn't have country code, add 91
    if (phone.length === 10) {
      phone = '91' + phone;
    }
    
    if (!phone) {
      toast.error('Customer phone number not available');
      return;
    }
    
    // Encode message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // Open WhatsApp Web with the message
    const whatsappUrl = `https://web.whatsapp.com/send?phone=${phone}&text=${encodedMessage}`;
    
    // Open in new tab
    window.open(whatsappUrl, '_blank');
    
    toast.success(`Opening WhatsApp for ${getDispatchName(selectedOrder)}`);
    
    // Mark as details sent
    try {
      await axios.put(`${API}/orders/${selectedOrder.id}`, { is_details_sent: true });
      setOrders(prev => prev.map(o => o.id === selectedOrder.id ? { ...o, is_details_sent: true } : o));
      setSelectedOrder(prev => ({ ...prev, is_details_sent: true }));
    } catch (error) {
    }
  };

  // Calculate order totals
  const getOrderTotals = (order) => {
    if (!order) return { qty: 0, value: 0 };
    const qty = order.items?.length > 0 
      ? order.items.reduce((sum, i) => sum + (i.qty || 0), 0) 
      : (order.total_qty || order.quantity_kg || 0);
    const value = (() => {
      const discountTerms = ['ADV', 'DDAP', 'DDAP RECVD'];
      const pt = (order.payment_terms || '').toUpperCase();
      if (discountTerms.includes(pt) && order.total_discount > 0) {
        return (order.total_value || 0) - (order.total_discount || 0);
      }
      return order.items?.length > 0 
        ? order.items.reduce((sum, i) => sum + (i.amount || i.qty * i.rate || 0), 0) 
        : (order.total_value || order.total_price || 0);
    })();
    return { qty, value };
  };

  // 9kg grade logic for AVRG calculation
  const NINE_KG_GRADES = ['JH', 'SJH', 'JJH', 'W150', 'W180', '180', 'A180', 'PG'];
  const getPackingKg = (item) => {
    const grade = (item.grade || '').toUpperCase().trim();
    const sku = (item.sku || item.packing || 'TIN').toUpperCase().trim();
    if (packSizes[`${grade}|${sku}`]) return packSizes[`${grade}|${sku}`];
    if (packSizes[grade]) return packSizes[grade];
    return NINE_KG_GRADES.includes(grade) ? 9 : 10;
  };
  const getOrderTotalKgs = (order) => {
    return (order?.items || []).reduce((s, i) => s + ((i.qty || 0) * getPackingKg(i)), 0);
  };

  // Day Dispatch AVRG (using invoice value after discount, excl vacuum & tin_rej)
  const isExcludedSku = (i) => { const s = (i.sku || i.packing || '').toUpperCase().replace(/ /g, '_'); return s.startsWith('VACUUM') || s === 'TIN_REJ'; };
  const totalDispatchKgs = dispatchedOrders.reduce((sum, o) => {
    return sum + (o?.items || []).filter(i => !isExcludedSku(i)).reduce((s, i) => s + ((i.qty || 0) * getPackingKg(i)), 0);
  }, 0);
  const totalDispatchValue = dispatchedOrders.reduce((sum, o) => {
    const allItems = o?.items || [];
    const nonVac = allItems.filter(i => !isExcludedSku(i));
    const filteredVal = nonVac.reduce((s, i) => s + ((i.qty || 0) * (i.rate || 0)), 0);
    if (o.total_discount > 0 && filteredVal > 0) {
      const allVal = allItems.reduce((s, i) => s + ((i.qty || 0) * (i.rate || 0)), 0);
      if (allVal > 0) return sum + filteredVal - (o.total_discount * (filteredVal / allVal));
    }
    return sum + filteredVal;
  }, 0);
  const dayDispatchAvrg = totalDispatchKgs > 0 ? totalDispatchValue / totalDispatchKgs : 0;

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  return (
    <div className="p-4" data-testid="crm-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <Users className="w-5 h-5" /> CRM - Dispatched Summary
          {dayDispatchAvrg > 0 && <span className="text-sm font-bold text-blue-700 ml-2" data-testid="crm-day-dispatch-avrg">| Day AVRG: {formatCurrency(Math.round(dayDispatchAvrg))}</span>}
        </h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              const dateStr = format(selectedDate, 'yyyy-MM-dd');
              const response = await axios.get(`${API}/dispatch-excel?date=${dateStr}`, { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([response.data]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', `dispatch_qty_${dateStr}.xlsx`);
              document.body.appendChild(link);
              link.click();
              link.remove();
              window.URL.revokeObjectURL(url);
              toast.success(`Downloaded dispatch Excel for ${formatDateDDMonYY(selectedDate)}`);
            } catch (err) {
              toast.error("Failed to download Excel");
            }
          }}>
            <Download className="w-4 h-4 mr-1" /> Day Dispatch Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => setInvoiceDialogOpen(true)} data-testid="generate-invoice-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Generate Invoice
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Date Selection + View Mode Toggle */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline" className="gap-1 h-8">
                <CalendarIcon className="w-3 h-3" />
                {formatDateDDMonYY(selectedDate)}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={selectedDate} onSelect={(date) => date && setSelectedDate(date)} initialFocus />
          </PopoverContent>
        </Popover>
        <span className="text-sm text-muted-foreground">
          {dispatchedOrders.length} orders dispatched
        </span>
        {(saving || savingTrip) && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </div>
        
        {/* View Mode Toggle */}
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant={viewMode === 'orders' ? 'default' : 'outline'}
            onClick={() => setViewMode('orders')}
            className="h-8"
          >
            <Truck className="w-4 h-4 mr-1" /> Dispatch Orders
          </Button>
          <Button 
            size="sm" 
            variant={viewMode === 'trip_summary' ? 'default' : 'outline'}
            onClick={() => setViewMode('trip_summary')}
            className="h-8"
          >
            <ClipboardList className="w-4 h-4 mr-1" /> Trip Summary
          </Button>
        </div>
      </div>

      {/* ============ VIEW MODE: ORDERS ============ */}
      {viewMode === 'orders' && (
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        {/* Orders List - Left Side */}
        <Card className="lg:col-span-3">
          <CardContent className="p-0">
            <div className="p-2 border-b bg-green-50 flex justify-between items-center">
              <span className="font-bold text-green-800 flex items-center gap-1">
                <Truck className="w-4 h-4" /> Dispatched Orders - {format(selectedDate, "dd-MMM-yy")}
              </span>
              <div className="flex gap-3 text-xs">
                <span className="font-semibold">Total Qty: <span className="text-green-700">{dispatchedOrders.reduce((sum, o) => sum + getOrderTotals(o).qty, 0)}</span></span>
                <span className="font-semibold">KGs: <span className="text-green-700">{totalDispatchKgs.toLocaleString('en-IN')}</span></span>
                <span className="font-semibold">Total Value: <span className="text-green-700">{formatCurrency(dispatchedOrders.reduce((sum, o) => sum + getOrderTotals(o).value, 0))}</span></span>
                {dayDispatchAvrg > 0 && <span className="font-semibold">AVRG: <span className="text-blue-700">{formatCurrency(Math.round(dayDispatchAvrg))}</span></span>}
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : dispatchedOrders.length > 0 ? (
              <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/50">
                      <TableHead className="text-[9px] py-1 h-6 w-[25px]">Trip</TableHead>
                      <TableHead className="text-[9px] py-1 h-6 w-[50px]">Order</TableHead>
                      <TableHead className="text-[9px] py-1 h-6 w-[50px]">Dispatch</TableHead>
                      <TableHead className="text-[9px] py-1 h-6 w-[80px]">Customer</TableHead>
                      <TableHead className="text-[9px] py-1 h-6 w-[30px] text-right">Qty</TableHead>
                      <TableHead className="text-[9px] py-1 h-6 w-[55px] text-right">Value</TableHead>
                      <TableHead className="text-[9px] py-1 h-6 w-[60px]">Transport</TableHead>
                      <TableHead className="text-[9px] py-1 h-6 w-[45px]">LR</TableHead>
                      <TableHead className="text-[9px] py-1 h-6 w-[45px]">Invoice</TableHead>
                      <TableHead className="text-[9px] py-1 h-6 w-[50px]">INV Sent</TableHead>
                      <TableHead className="text-[9px] py-1 h-6 w-[25px] text-center" title="Posted to Dispatch Sheet">
                        <FileSpreadsheet className="w-3 h-3 mx-auto" />
                      </TableHead>
                      <TableHead className="text-[9px] py-1 h-6 w-[50px] text-center">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dispatchedOrders.map((order) => {
                      const { qty, value } = getOrderTotals(order);
                      const isSelected = selectedOrder?.id === order.id;
                      const isINV = order.est_type === 'INV';
                      const isPosted = order.dispatch_posted_to_sheets;
                      
                      // Check if dispatched more than 1 day ago
                      const dispatchDate = order.dispatched_at ? new Date(order.dispatched_at) : null;
                      const oneDayAgo = new Date();
                      oneDayAgo.setDate(oneDayAgo.getDate() - 1);
                      const isOverdue = dispatchDate && dispatchDate < oneDayAgo;
                      
                      // Check if transport details are missing
                      const missingTransport = !order.transport_name;
                      const missingLR = !order.lr_number;
                      const missingInvoice = !order.invoice_number;
                      
                      // Highlight in red if overdue and missing details
                      const highlightTransport = isOverdue && missingTransport;
                      const highlightLR = isOverdue && missingLR;
                      
                      return (
                        <TableRow 
                          key={order.id} 
                          onClick={() => selectOrder(order)}
                          className={`cursor-pointer ${isSelected ? 'bg-primary/10' : isINV ? 'bg-yellow-50 hover:bg-yellow-100' : 'hover:bg-secondary/30'}`}
                        >
                          <TableCell className="text-[10px] py-1 font-semibold">
                            <span className="flex items-center gap-1">
                              {order.trip_no || '-'}
                              {(order.remark || order.notes) && <span className="inline-block w-2 h-2 rounded-full bg-orange-400 cursor-pointer" title={order.remark || order.notes} onClick={(e) => { e.stopPropagation(); toast.info(order.remark || order.notes, { duration: 5000 }); }}></span>}
                            </span>
                          </TableCell>
                          <TableCell className="text-[10px] py-1 font-mono font-bold">{order.order_number}</TableCell>
                          <TableCell className="text-[9px] py-1 font-mono font-semibold text-blue-600">{order.dispatch_number || '-'}</TableCell>
                          <TableCell className="text-[9px] py-1 truncate max-w-[80px]" title={getDispatchName(order)}>{getDispatchName(order)}</TableCell>
                          <TableCell className="text-[10px] py-1 text-right font-bold">{qty}</TableCell>
                          <TableCell className="text-[10px] py-1 text-right font-medium text-green-700">{formatCurrency(value)}</TableCell>
                          <TableCell className={`text-[9px] py-1 truncate ${highlightTransport ? 'bg-red-100 text-red-600 font-semibold' : 'text-muted-foreground'}`} title={highlightTransport ? 'Transport name required - dispatched over 1 day ago' : ''}>
                            {order.transport_name || (highlightTransport ? 'MISSING' : '-')}
                          </TableCell>
                          <TableCell className={`text-[9px] py-1 truncate ${highlightLR ? 'bg-red-100 text-red-600 font-semibold' : 'text-muted-foreground'}`} title={highlightLR ? 'LR number required - dispatched over 1 day ago' : ''}>
                            {order.lr_number || (highlightLR ? 'MISSING' : '-')}
                          </TableCell>
                          <TableCell className={`text-[9px] py-1 truncate ${isOverdue && missingInvoice ? 'bg-red-100 text-red-600 font-semibold' : 'text-muted-foreground'}`} title={isOverdue && missingInvoice ? 'Invoice number required - dispatched over 1 day ago' : ''}>
                            {order.invoice_number || (isOverdue && missingInvoice ? 'MISSING' : '-')}
                          </TableCell>
                          <TableCell className="py-1" onClick={(e) => e.stopPropagation()}>
                            <select
                              value={invSentLocal[order.id] ?? order.inv_sent ?? ''}
                              onChange={async (e) => {
                                const val = e.target.value;
                                setInvSentLocal(prev => ({ ...prev, [order.id]: val }));
                                try {
                                  await axios.put(`${API}/orders/${order.id}`, { inv_sent: val });
                                } catch (err) { /* silent */ }
                              }}
                              className={`text-[9px] w-full border-0 bg-transparent p-0 h-5 cursor-pointer focus:ring-0 ${(invSentLocal[order.id] ?? order.inv_sent) === 'Yes' ? 'text-green-700 font-semibold' : (invSentLocal[order.id] ?? order.inv_sent) === 'No' ? 'text-red-500' : 'text-muted-foreground'}`}
                              data-testid={`inv-sent-${order.id}`}
                            >
                              <option value="">-</option>
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          </TableCell>
                          <TableCell className="py-1 text-center" onClick={(e) => e.stopPropagation()}>
                            {postingToSheets === order.id ? (
                              <Loader2 className="w-4 h-4 animate-spin mx-auto text-blue-500" />
                            ) : isPosted ? (
                              <CheckCircle className="w-4 h-4 text-green-500 mx-auto cursor-pointer" title="Posted to Dispatch Sheet" onClick={() => postDispatchToSheets(order)} />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-400 mx-auto cursor-pointer hover:text-red-600" title="Click to post to Dispatch Sheet" onClick={() => postDispatchToSheets(order)} />
                            )}
                          </TableCell>
                          <TableCell className="py-1 text-center" onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => openEditDialog(order)} title="Edit Order">
                              <Edit className="w-3 h-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No dispatched orders on {format(selectedDate, "dd-MMM-yy")}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Side Panel - Right Side */}
        <Card className="lg:col-span-2">
          <CardContent className="p-3">
            {selectedOrder ? (
              <div className="space-y-3">
                {/* Order Header */}
                <div className="flex items-center justify-between border-b pb-2">
                  <div>
                    <h3 className="font-bold text-lg">{selectedOrder.order_number}</h3>
                    <p className="text-sm text-muted-foreground">{getDispatchName(selectedOrder)}</p>
                    {selectedOrder.dispatch_number && (
                      <p className="text-xs text-blue-600 font-mono font-semibold">Dispatch: {selectedOrder.dispatch_number}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => printEstimate(selectedOrder)} title="Print Estimate">
                      <Printer className="w-3.5 h-3.5 text-blue-600" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => downloadEstimatePDF(selectedOrder)} title="Download PDF">
                      <Download className="w-3.5 h-3.5 text-green-600" />
                    </Button>
                    <Badge className="bg-green-100 text-green-800">{selectedOrder.trip_no || '-'}</Badge>
                  </div>
                </div>

                {/* Order Summary Table - Grades editable for print only */}
                <div className="border rounded overflow-hidden">
                  <div className="px-2 py-1 bg-green-50 text-xs font-semibold border-b flex items-center justify-between">
                    <span>Order Summary</span>
                    <span className="flex items-center gap-1">
                      {printGradeOverrides[selectedOrder.id] && (
                        <button
                          onClick={() => resetPrintOverrides(selectedOrder.id)}
                          className="text-[9px] text-red-500 hover:text-red-700 underline"
                          data-testid="reset-print-grades"
                        >
                          Reset Grades
                        </button>
                      )}
                      <span className="text-[9px] text-muted-foreground italic">edit grade for print</span>
                    </span>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-secondary/30">
                        <TableHead className="text-[10px] py-1 h-6">Grade</TableHead>
                        <TableHead className="text-[10px] py-1 h-6">Type</TableHead>
                        <TableHead className="text-[10px] py-1 h-6 text-right">Qty</TableHead>
                        <TableHead className="text-[10px] py-1 h-6 text-right">Rate</TableHead>
                        <TableHead className="text-[10px] py-1 h-6 text-right">Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedOrder.items?.length > 0 ? (
                        selectedOrder.items.map((item, idx) => {
                          const overrideGrade = printGradeOverrides[selectedOrder.id]?.[idx]?.grade;
                          const overrideSku = printGradeOverrides[selectedOrder.id]?.[idx]?.sku;
                          const displayGrade = overrideGrade ?? item.grade;
                          const displaySku = overrideSku ?? item.sku;
                          const isOverridden = (overrideGrade !== undefined && overrideGrade !== item.grade) || (overrideSku !== undefined && overrideSku !== item.sku);
                          return (
                            <TableRow key={`${selectedOrder.id}-${idx}`}>
                              <TableCell className="text-[10px] py-0.5 px-1">
                                <input
                                  type="text"
                                  value={displayGrade}
                                  onChange={(e) => updatePrintGrade(selectedOrder.id, idx, e.target.value)}
                                  className={`w-full bg-transparent border-b border-dashed text-[10px] px-0.5 py-0.5 outline-none focus:border-blue-400 ${isOverridden ? 'text-blue-600 font-semibold border-blue-300' : 'border-transparent'}`}
                                  title="Edit grade for print only (not saved)"
                                  data-testid={`print-grade-input-${idx}`}
                                />
                              </TableCell>
                              <TableCell className="text-[10px] py-0.5 px-1">
                                <input
                                  type="text"
                                  value={displaySku}
                                  onChange={(e) => updatePrintSku(selectedOrder.id, idx, e.target.value)}
                                  className={`w-full bg-transparent border-b border-dashed text-[10px] px-0.5 py-0.5 outline-none focus:border-blue-400 ${isOverridden ? 'text-blue-600 font-semibold border-blue-300' : 'border-transparent'}`}
                                  title="Edit type for print only (not saved)"
                                  data-testid={`print-sku-input-${idx}`}
                                />
                              </TableCell>
                              <TableCell className="text-[10px] py-1 text-right font-medium">{item.qty}</TableCell>
                              <TableCell className="text-[10px] py-1 text-right">{formatCurrency(item.rate)}</TableCell>
                              <TableCell className="text-[10px] py-1 text-right font-medium">{formatCurrency(item.amount || item.qty * item.rate)}</TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell className="text-[10px] py-1">{selectedOrder.product_grade}</TableCell>
                          <TableCell className="text-[10px] py-1">{selectedOrder.packing}</TableCell>
                          <TableCell className="text-[10px] py-1 text-right font-medium">{selectedOrder.quantity_kg}</TableCell>
                          <TableCell className="text-[10px] py-1 text-right">{formatCurrency(selectedOrder.unit_price)}</TableCell>
                          <TableCell className="text-[10px] py-1 text-right font-medium">{formatCurrency(selectedOrder.total_price)}</TableCell>
                        </TableRow>
                      )}
                      {/* Total Row */}
                      <TableRow className="bg-green-50 font-bold">
                        <TableCell colSpan={2} className="text-[10px] py-1">TOTAL</TableCell>
                        <TableCell className="text-[10px] py-1 text-right">{getOrderTotals(selectedOrder).qty}</TableCell>
                        <TableCell className="text-[10px] py-1"></TableCell>
                        <TableCell className="text-[10px] py-1 text-right text-green-700">{formatCurrency(getOrderTotals(selectedOrder).value)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                {/* Editable Details - REMOVED - Now show formatted Estimate View */}
                {/* Dispatch Details Section - Display Only (entered in Trip Summary) */}
                <div className="border rounded overflow-hidden">
                  <div className="px-2 py-1.5 bg-blue-50 text-xs font-semibold border-b text-blue-800">Dispatch Details</div>
                  <div className="p-2 space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Transport:</span>
                      <span className="font-medium">{selectedOrder.transport_name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">LR Number:</span>
                      <span className="font-medium">{selectedOrder.lr_number || '-'}</span>
                    </div>
                  </div>
                  <div className="px-2 py-1 bg-gray-50 border-t text-[9px] text-muted-foreground text-center">
                    Edit dispatch details in Trip Summary
                  </div>
                </div>

                {/* Discount & Final Total Section */}
                <div className="border rounded overflow-hidden">
                  <div className="px-2 py-1.5 bg-purple-50 text-xs font-semibold border-b text-purple-800">Payment Summary</div>
                  <div className="p-2 space-y-1.5 text-xs">
                    {(() => {
                      const rawTotal = selectedOrder.items?.length > 0
                        ? selectedOrder.items.reduce((sum, i) => sum + (i.amount || i.qty * i.rate || 0), 0)
                        : (selectedOrder.total_value || selectedOrder.total_price || 0);
                      // Auto-calculate discount for ADV/DDAP if not already saved
                      let discount = selectedOrder.total_discount || 0;
                      const pt = (selectedOrder.payment_terms || '').toUpperCase();
                      if (discount === 0 && ['ADV', 'DDAP', 'DDAP RECVD'].includes(pt)) {
                        const discPerKg = selectedOrder.discount_per_kg || 10;
                        if (selectedOrder.items?.length > 0) {
                          selectedOrder.items.forEach(item => {
                            const packKg = item.kgs_pack || (products.find(p => p.grade === item.grade && p.sku === item.sku)?.kgs_pack) || 10;
                            discount += discPerKg * packKg * (item.qty || 0);
                          });
                        }
                      }
                      const finalTotal = rawTotal - discount;
                      return (
                        <>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Total Value:</span>
                            <span className="font-medium">{formatCurrency(rawTotal)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Discount Given:</span>
                            <span className="font-medium text-red-600">- {formatCurrency(discount)}</span>
                          </div>
                          <div className="flex justify-between border-t pt-1.5 mt-1.5">
                            <span className="font-semibold">Final Total:</span>
                            <span className="font-bold text-green-700">{formatCurrency(finalTotal)}</span>
                          </div>
                        </>
                      );
                    })()}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Payment Terms:</span>
                      <Badge variant="outline" className="text-[9px]">{selectedOrder.payment_terms || '-'}</Badge>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="flex-1 h-8"
                    onClick={printInvoice}
                  >
                    <Printer className="w-3 h-3 mr-1" /> Print
                  </Button>
                  <Button 
                    size="sm"
                    className="flex-1 h-8 bg-green-600 hover:bg-green-700"
                    onClick={sendInvoice}
                  >
                    <Send className="w-3 h-3 mr-1" /> Send
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[350px] text-muted-foreground">
                <Package className="w-12 h-12 mb-3 opacity-30" />
                <p className="text-sm font-medium">Select an order</p>
                <p className="text-xs mt-1">Click on an order to view details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* ============ VIEW MODE: TRIP SUMMARY ============ */}
      {viewMode === 'trip_summary' && (
        <div className="space-y-4">
          {/* Pending Incomplete Dispatches Button */}
          {!loading && getIncompleteDispatches().length > 0 && (
            <div className="flex justify-end">
              <Button 
                size="sm" 
                variant="outline" 
                className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => setPendingDialogOpen(true)}
                data-testid="pending-dispatches-btn"
              >
                <XCircle className="w-3.5 h-3.5 mr-1 text-red-500" />
                {getIncompleteDispatches().length} Pending Dispatch Details
              </Button>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : Object.keys(tripGroups).length > 0 ? (
            Object.entries(tripGroups)
              .sort(([a], [b]) => {
                const tripOrder = ['T1', 'T2', 'T3', 'T4', 'T5'];
                const idxA = tripOrder.indexOf(a) >= 0 ? tripOrder.indexOf(a) : 999;
                const idxB = tripOrder.indexOf(b) >= 0 ? tripOrder.indexOf(b) : 999;
                return idxA - idxB;
              })
              .map(([tripNo, tripOrders]) => {
                const tripQty = tripOrders.reduce((sum, o) => sum + getOrderTotals(o).qty, 0);
                const tripValue = tripOrders.reduce((sum, o) => sum + getOrderTotals(o).value, 0);
                const hasEdits = tripHasUnsavedEdits(tripNo);
                const posted = isTripPosted(tripNo);
                const incomplete = tripHasIncomplete(tripNo);
                
                return (
                  <Card key={tripNo} className={hasEdits ? 'border-yellow-400' : ''}>
                    <CardContent className="p-0">
                      {/* Trip Header - Compact */}
                      <div className="px-2 py-1.5 bg-green-50 border-b flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <Badge className="bg-green-600 text-white text-xs px-2">{tripNo}</Badge>
                            {incomplete && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full" title="Incomplete dispatch details" />}
                          </div>
                          <span className="text-xs font-semibold text-green-800">{tripOrders.length} orders</span>
                          <span className="text-xs text-muted-foreground">| {tripQty}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {hasEdits && (
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700 h-6 text-xs px-2"
                              onClick={() => saveAllTripOrders(tripNo)}
                              disabled={savingTrip === tripNo}
                            >
                              {savingTrip === tripNo ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                              Save All
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => postTripToSheets(tripNo)}
                            disabled={postingTrip === tripNo}
                            title={posted ? "Posted to Google Sheets (click to repost)" : "Post trip to Google Sheets"}
                            data-testid={`trip-post-sheets-${tripNo}`}
                          >
                            {postingTrip === tripNo ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <FileSpreadsheet className={`w-4 h-4 ${posted ? 'text-green-600' : 'text-red-500'}`} />
                            )}
                          </Button>
                        </div>
                      </div>
                      
                      {/* Orders Table - Compact with Bill Type */}
                      <div className="p-1">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-secondary/30">
                              <TableHead className="text-xs py-0.5 h-6 px-1 w-[50px]">Order</TableHead>
                              <TableHead className="text-xs py-0.5 h-6 px-1 w-[55px]">Dispatch</TableHead>
                              <TableHead className="text-xs py-0.5 h-6 px-1 w-[90px]">Customer</TableHead>
                              <TableHead className="text-xs py-0.5 h-6 px-1 w-[30px] text-right">Qty</TableHead>
                              <TableHead className="text-xs py-0.5 h-6 px-1 w-[100px]">Transport</TableHead>
                              <TableHead className="text-xs py-0.5 h-6 px-1 w-[60px]">LR No</TableHead>
                              <TableHead className="text-xs py-0.5 h-6 px-1 w-[35px]">Bill</TableHead>
                              <TableHead className="text-xs py-0.5 h-6 px-1 w-[70px]">EST/INV Num</TableHead>
                              <TableHead className="text-xs py-0.5 h-6 px-1 w-[30px] text-center">PDF</TableHead>
                              <TableHead className="text-xs py-0.5 h-6 px-1 w-[30px]"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {tripOrders.map((order) => {
                              const { qty } = getOrderTotals(order);
                              const orderHasEdits = hasUnsavedEdits(order.id);
                              const isInvoice = order.est_type === 'INV';
                              const isEditing = editingTripOrderId === order.id;
                              
                              return (
                                <TableRow key={order.id} className={`${isInvoice ? 'bg-yellow-200 border-l-4 border-l-yellow-500' : ''} ${orderHasEdits ? 'bg-amber-50' : ''}`}>
                                  <TableCell className="text-xs py-0.5 px-1 font-mono font-bold">{order.order_number}</TableCell>
                                  <TableCell className="text-xs py-0.5 px-1 font-mono text-[9px] text-muted-foreground">{order.dispatch_number || '-'}</TableCell>
                                  <TableCell className="text-xs py-0.5 px-1 truncate max-w-[90px]" title={getDispatchName(order)}>{getDispatchName(order)}</TableCell>
                                  <TableCell className="text-xs py-0.5 px-1 text-right font-semibold">{qty}</TableCell>
                                  <TableCell className="py-0 px-0.5">
                                    {isEditing ? (
                                      <Input
                                        value={getTripFieldValue(order, 'transport_name')}
                                        onChange={(e) => handleTripFieldChange(order.id, 'transport_name', e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); saveTripOrderDetails(order.id); } }}
                                        placeholder="Transport..."
                                        className="h-5 text-xs px-1"
                                        autoFocus
                                      />
                                    ) : (
                                      <span className={`text-xs px-1 ${order.transport_name ? '' : 'text-muted-foreground'}`}>{order.transport_name || 'NA'}</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="py-0 px-0.5">
                                    {isEditing ? (
                                      <Input
                                        value={getTripFieldValue(order, 'lr_number')}
                                        onChange={(e) => handleTripFieldChange(order.id, 'lr_number', e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); saveTripOrderDetails(order.id); } }}
                                        placeholder="LR..."
                                        className="h-5 text-xs px-1"
                                      />
                                    ) : (
                                      <span className={`text-xs px-1 ${order.lr_number ? '' : 'text-muted-foreground'}`}>{order.lr_number || 'NA'}</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="py-0.5 px-1">
                                    <Badge variant="outline" className={`text-[9px] h-4 px-1 ${isInvoice ? 'bg-yellow-200 text-yellow-800 border-yellow-400' : 'bg-blue-50 text-blue-700 border-blue-300'}`}>
                                      {order.est_type || 'EST'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="py-0 px-0.5">
                                    {isEditing ? (
                                      <Input
                                        value={getTripFieldValue(order, 'invoice_number')}
                                        onChange={(e) => handleTripFieldChange(order.id, 'invoice_number', e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); saveTripOrderDetails(order.id); } }}
                                        placeholder={isInvoice ? "INV..." : "EST..."}
                                        className="h-5 text-xs px-1"
                                      />
                                    ) : (
                                      <span className={`text-xs px-1 ${order.invoice_number ? '' : 'text-muted-foreground'}`}>{order.invoice_number || 'NA'}</span>
                                    )}
                                  </TableCell>
                                  {/* Invoice PDF Upload/View/Delete */}
                                  <TableCell className="py-0 px-0.5 text-center">
                                    {order.invoice_pdf_path ? (
                                      <div className="flex items-center justify-center gap-0.5">
                                        <button onClick={() => window.open(`${API}/dispatch/${order.dispatch_number}/invoice`, '_blank')}
                                          className="text-green-600 hover:text-green-800" title="View Invoice PDF">
                                          <FileCheck className="w-3.5 h-3.5" />
                                        </button>
                                        {(() => {
                                          try {
                                            const u = JSON.parse(localStorage.getItem('user') || '{}');
                                            if (['owner', 'admin'].includes(u.role)) {
                                              return (
                                                <button onClick={() => {
                                                  if (!window.confirm(`Delete invoice PDF for ${order.dispatch_number}?`)) return;
                                                  const dn = order.dispatch_number;
                                                  axios.delete(`${API}/dispatch/${dn}/invoice`).then(() => {
                                                    toast.success(`Invoice PDF deleted for ${dn}`);
                                                    setOrders(prev => prev.map(o => o.dispatch_number === dn ? {...o, invoice_pdf_path: null} : o));
                                                  }).catch(err => toast.error(err.response?.data?.detail || 'Delete failed'));
                                                }} className="text-red-400 hover:text-red-600" title="Delete PDF">
                                                  <Trash2 className="w-3 h-3" />
                                                </button>
                                              );
                                            }
                                          } catch { /* */ }
                                          return null;
                                        })()}
                                      </div>
                                    ) : order.dispatch_number ? (
                                      <label className="cursor-pointer text-muted-foreground hover:text-blue-600" title="Upload Invoice PDF">
                                        <Upload className="w-3.5 h-3.5" />
                                        <input type="file" accept=".pdf" className="hidden" onChange={(e) => {
                                          const file = e.target.files?.[0];
                                          if (!file) return;
                                          e.target.value = '';
                                          const fd = new FormData();
                                          fd.append('file', file);
                                          const dn = order.dispatch_number;
                                          axios.post(`${API}/dispatch/${dn}/upload-invoice`, fd).then(res => {
                                            toast.success(`Invoice uploaded for ${dn}`);
                                            setOrders(prev => prev.map(o => o.dispatch_number === dn ? {...o, invoice_pdf_path: res.data?.file?.storage_path || 'uploaded'} : o));
                                          }).catch(err => toast.error(err.response?.data?.detail || 'Upload failed'));
                                        }} />
                                      </label>
                                    ) : <span className="text-[9px] text-muted-foreground">-</span>}
                                  </TableCell>
                                  <TableCell className="py-0 px-0.5 text-center">
                                    {isEditing ? (
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        className="h-5 w-5 p-0"
                                        onClick={() => saveTripOrderDetails(order.id)}
                                        disabled={savingTrip === order.id}
                                        title="Save"
                                      >
                                        {savingTrip === order.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                      </Button>
                                    ) : (
                                      <Button 
                                        size="sm" 
                                        variant="ghost"
                                        className="h-5 w-5 p-0"
                                        onClick={() => setEditingTripOrderId(order.id)}
                                        title="Edit"
                                      >
                                        <Edit className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No dispatched orders on {format(selectedDate, "dd-MMM-yy")}</p>
            </div>
          )}
        </div>
      )}

      {/* Edit Order Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Dispatched Order - {editingOrder?.order_number}
            </DialogTitle>
          </DialogHeader>
          
          {editingOrder && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Customer Name</Label>
                  <Input 
                    value={editingOrder.customer_name || ''} 
                    onChange={(e) => updateEditingOrder('customer_name', e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Order Date</Label>
                  <DateInputDDMonYY
                    value={editingOrder.order_date || ''} 
                    onChange={(e) => updateEditingOrder('order_date', e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Payment Terms</Label>
                  <Select value={editingOrder.payment_terms || ''} onValueChange={(v) => updateEditingOrder('payment_terms', v)}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="COD">COD</SelectItem>
                      <SelectItem value="ADV">ADV</SelectItem>
                      <SelectItem value="DDAP">DDAP</SelectItem>
                      <SelectItem value="DDAP RECVD">DDAP RECVD</SelectItem>
                      <SelectItem value="CREDIT">CREDIT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Bill Type</Label>
                  <Select value={editingOrder.est_type || 'EST'} onValueChange={(v) => updateEditingOrder('est_type', v)}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EST">EST</SelectItem>
                      <SelectItem value="INV">INV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Items Table */}
              <div className="border rounded">
                <div className="px-3 py-2 bg-secondary/50 border-b flex justify-between items-center">
                  <span className="text-sm font-semibold">Order Items</span>
                  <Button size="sm" variant="outline" className="h-6 text-xs" onClick={addEditingItem}>
                    <Plus className="w-3 h-3 mr-1" /> Add Row
                  </Button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs py-1 w-[100px]">Grade</TableHead>
                      <TableHead className="text-xs py-1 w-[80px]">SKU</TableHead>
                      <TableHead className="text-xs py-1 w-[70px]">Qty</TableHead>
                      <TableHead className="text-xs py-1 w-[90px]">Rate (₹)</TableHead>
                      <TableHead className="text-xs py-1 w-[100px] text-right">Amount</TableHead>
                      <TableHead className="text-xs py-1 w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {editingOrder.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="py-1">
                          <Select value={item.grade || ''} onValueChange={(v) => updateEditingItem(idx, 'grade', v)}>
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder="Grade" />
                            </SelectTrigger>
                            <SelectContent>
                              {uniqueGrades.map(g => (
                                <SelectItem key={g} value={g}>{g}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="py-1">
                          <Select value={item.sku || 'TIN'} onValueChange={(v) => updateEditingItem(idx, 'sku', v)}>
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="TIN">TIN</SelectItem>
                              <SelectItem value="BUCKET">BUCKET</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="py-1">
                          <Input 
                            type="number" 
                            value={item.qty || ''} 
                            onChange={(e) => updateEditingItem(idx, 'qty', parseInt(e.target.value) || 0)}
                            className="h-7 text-xs"
                          />
                        </TableCell>
                        <TableCell className="py-1">
                          <Input 
                            type="number" 
                            value={item.rate || ''} 
                            onChange={(e) => updateEditingItem(idx, 'rate', parseInt(e.target.value) || 0)}
                            className="h-7 text-xs"
                          />
                        </TableCell>
                        <TableCell className="py-1 text-right font-semibold text-sm">
                          {formatCurrency((item.qty || 0) * (item.rate || 0))}
                        </TableCell>
                        <TableCell className="py-1">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => removeEditingItem(idx)} disabled={editingOrder.items.length <= 1}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Discount Info */}
              <div className="grid grid-cols-3 gap-3 p-3 bg-secondary/20 rounded border">
                <div>
                  <Label className="text-xs">Total Value</Label>
                  <div className="text-lg font-bold text-green-700">
                    {formatCurrency(editingOrder.items?.reduce((sum, i) => sum + ((i.qty || 0) * (i.rate || 0)), 0) || 0)}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Discount (₹10/kg)</Label>
                  <div className="text-lg font-bold text-red-600">
                    {['ADV', 'DDAP', 'DDAP RECVD'].includes(editingOrder.payment_terms) ? (
                      <>- {formatCurrency(editingOrder.items?.reduce((sum, i) => {
                        return sum + (10 * getPackingKg(i) * (i.qty || 0));
                      }, 0) || 0)}</>
                    ) : '-'}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Final Amount</Label>
                  <div className="text-lg font-bold text-blue-700">
                    {(() => {
                      const total = editingOrder.items?.reduce((sum, i) => sum + ((i.qty || 0) * (i.rate || 0)), 0) || 0;
                      let discount = 0;
                      if (['ADV', 'DDAP', 'DDAP RECVD'].includes(editingOrder.payment_terms)) {
                        discount = editingOrder.items?.reduce((sum, i) => {
                          return sum + (10 * getPackingKg(i) * (i.qty || 0));
                        }, 0) || 0;
                      }
                      return formatCurrency(total - discount);
                    })()}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button onClick={saveEditedOrder} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pending Dispatch Details Dialog */}
      <Dialog open={pendingDialogOpen} onOpenChange={setPendingDialogOpen}>
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <XCircle className="w-5 h-5" />
              Pending Dispatch Details
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1">
            {getIncompleteDispatches().map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 border rounded text-xs bg-red-50/50">
                <div>
                  <span className="font-mono font-bold">{item.dispatch_number}</span>
                  <span className="text-muted-foreground ml-2">({item.order_number})</span>
                  <span className="ml-2 text-muted-foreground">{item.customer}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Badge variant="outline" className="text-[9px] h-4">{item.trip}</Badge>
                  <span className="text-red-600 text-[10px]">{item.missing.join(', ')}</span>
                </div>
              </div>
            ))}
            {getIncompleteDispatches().length === 0 && (
              <p className="text-center text-muted-foreground py-4">All dispatch details are complete</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Generation Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={(open) => { setInvoiceDialogOpen(open); if (!open) setInvoiceError(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Generate Invoice (FORMAT-2)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold">Dispatch Number</Label>
              <Input
                value={invoiceDispatchNo}
                onChange={(e) => { setInvoiceDispatchNo(e.target.value.toUpperCase()); setInvoiceError(''); }}
                placeholder="e.g. D03086"
                className="mt-1"
                data-testid="invoice-dispatch-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Enter dispatch number (e.g. D03086) or order number (e.g. E03001)</p>
            </div>
            <div>
              <Label className="text-xs font-semibold">Upload e-Invoice (FORMAT-1 PDF)</Label>
              <Input
                type="file"
                accept=".pdf"
                onChange={(e) => { setInvoiceFile(e.target.files[0]); setInvoiceError(''); }}
                className="mt-1"
                data-testid="invoice-file-input"
              />
              {invoiceFile && <p className="text-[10px] text-green-700 mt-1">{invoiceFile.name}</p>}
            </div>
            {invoiceError && (
              <div className="bg-red-50 border border-red-200 rounded p-2" data-testid="invoice-error-msg">
                <p className="text-xs text-red-700">{invoiceError}</p>
              </div>
            )}
            <Button 
              className="w-full" 
              onClick={handleGenerateInvoice}
              disabled={generatingInvoice || !invoiceDispatchNo.trim() || !invoiceFile}
              data-testid="invoice-generate-btn"
            >
              {generatingInvoice ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {generatingInvoice ? 'Generating...' : 'Generate & Download PDF'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
